import { DividerWithStatus } from "csi/DividerWithStatus";
import { ControllerInterface } from "csi/action/Controller";
import JsonResponse from "csi/comm/JsonResponse";
import { SlimCardStore } from "csi/middleware/card/SlimCardStore";
import { SlimCardWithStatus } from "csi/middleware/card/SlimCardWithStatus";
import { ControllerSaveFunction } from "csi/middleware/common";
import { getSearch } from "csi/service/dataStoreService";
import { submitWriter } from "csi/service/writerService";
import { C8Node, ICache, cnl } from "csi/typings";
import xml from "csi/xml";
import Writer from "csi/xml/Writer";
import { CheckboxTreeNode } from "designSystem/components/checkbox/CheckboxTree";
import { ILocaleProvider } from "designSystem/locale";
import { black, grey } from "designSystem/styles/color";
import { RestoreFromShapeStore } from "./RestoreFromShapeStore";

export function actionResource(res: string): string {
	return `RestoreFromShape_${res}`;
}

/** Creates and populates a CheckboxTreeNode with DSLines data */
async function createDSLine(dimension: C8Node, cache: ICache, locale: ILocaleProvider, statusLabelRes: string, state: boolean): Promise<CheckboxTreeNode> {
	const checkBoxNode = new CheckboxTreeNode(undefined, dimension.$URL, null, state);
	const textColor = state ? black[100] : grey[800];
	if (dimension.$Type !== "SizeChartDimension") {
		checkBoxNode.label = DividerWithStatus({
			name: locale.getNodeName(dimension),
			statusLabelRes,
			textColor,
		});
	} else {
		checkBoxNode.label = SlimCardWithStatus({
			displayImage: true,
			slimCardStore: new SlimCardStore(dimension.$URL, cache),
			statusLabelRes,
			textColor,
		});
	}
	return checkBoxNode;
}

/** Create a CheckboxTreeNode for the Style SC DSLines that were recently unlinked from Shape SC */
const createDeleteDSLinesTree = async (styleSCRevURL: cnl, locale: ILocaleProvider, cache: ICache): Promise<CheckboxTreeNode> => {
	const deleteDSLines = new CheckboxTreeNode(undefined, "Delete", locale.resource(actionResource("SectionDelete")), true);
	const response = await getSearch("DSLine.SizeChartRevision.DSLinesOnSizeChartRevision", styleSCRevURL, { wasLinkedToShape: true })
		.execute();

	response.getNodes()
		.forEach(async (d) => {
			const dimTreeNode = await createDSLine(d, cache, locale, actionResource("StatusDelete"), false);
			deleteDSLines.addChild(dimTreeNode);
		});

	return deleteDSLines;
};

/** Create a CheckboxTreeNode for the Style SC DSLines linked to Shape SC */
const createUpdateDSLinesTree = async (styleSCRevURL: cnl, locale: ILocaleProvider, cache: ICache): Promise<CheckboxTreeNode> => {
	const updateDSLines = new CheckboxTreeNode(undefined, "Update", locale.resource(actionResource("SectionUpdate")), true);
	const response = await getSearch("DSLine.SizeChartRevision.DSLinesOnSizeChartRevision", styleSCRevURL, { isLinkedToShape: true })
		.execute();

	response.getNodes()
		.forEach(async (d) => {
			const dimTreeNode = await createDSLine(d, cache, locale, actionResource("StatusUpdate"), true);
			updateDSLines.addChild(dimTreeNode);
		});

	return updateDSLines;
};

/** Create a CheckboxTreeNode for the Shape SC DSLines that are new and do not exist on Style SC yet */
const createAddDSLinesTree = async (styleSCRevURL: cnl, locale: ILocaleProvider, cache: ICache): Promise<CheckboxTreeNode> => {
	const addDSLines = new CheckboxTreeNode(undefined, "Add", locale.resource(actionResource("SectionAdd")), true);

	const responseStyleDSLines = await getSearch("DSLine.SizeChartRevision.DSLinesOnSizeChartRevision", styleSCRevURL)
		.execute();
	const styleSCDimsMap = new Map<string, boolean>(responseStyleDSLines.getNodes()
		.map((d) => {
			return [d.ShapeSysID, true];
		}));

	const responseShapeDSLines = await getSearch("DSLine.SizeChartRevision.LinkedShapeSizeChartDSLines", styleSCRevURL)
		.execute();
	responseShapeDSLines.getNodes()
		.forEach(async (d) => {
			if (!styleSCDimsMap.get(d.SysID)) {
				const dimTreeNode = await createDSLine(d, cache, locale, actionResource("StatusAdd"), true);
				addDSLines.addChild(dimTreeNode);
			}
		});

	return addDSLines;
};

/** Create and initialize RestoreFromShapeStore */
export const createRestoreFromShapeStore = async (ac: ControllerInterface, locale: ILocaleProvider, onSave: ControllerSaveFunction): Promise<RestoreFromShapeStore> => {
	const cache = ac.getCache();
	const styleSCRevURL = ac.m_urlParent;

	const addDSLines = await createAddDSLinesTree(styleSCRevURL, locale, cache);
	const deleteDSLines = await createDeleteDSLinesTree(styleSCRevURL, locale, cache);
	const updateDSLines = await createUpdateDSLinesTree(styleSCRevURL, locale, cache);
	const store = new RestoreFromShapeStore(ac, locale, onSave, addDSLines, deleteDSLines, updateDSLines);
	return store;
};

/** Send server request */
export async function restoreFromShape(
	styleSCRevURL: cnl, restoreAllDSLines: boolean, deleteDSLines: CheckboxTreeNode,
	updateDSLines: CheckboxTreeNode, addDSLines: CheckboxTreeNode, writer: Writer,
): Promise<JsonResponse> {
	writer
		.callMethod("ProductSpec", "RestoreSizeChartDimensionsFromShape")
		.addParameter("ParentURL", xml.ATTR_REF, styleSCRevURL)
		.addParameter("AddDSLines", xml.ATTR_REFLIST, restoreAllDSLines ? addDSLines.selectedNodes.map((s) => s.uniqueId) : [])
		.addParameter("UpdateDSLines", xml.ATTR_REFLIST, updateDSLines.selectedNodes.map((s) => s.uniqueId))
		.addParameter("DeleteDSLines", xml.ATTR_REFLIST, deleteDSLines.selectedNodes.map((s) => s.uniqueId));
	return submitWriter(writer);
}
